import { fetchProducts } from './productsService';

/**
 * Fetch the public artist roster (featured + verified artists only — the
 * API enforces this filter server-side).
 */
export const fetchAllArtists = async () => {
  const response = await fetch('/api/artists');
  const contentType = response.headers.get('content-type') || '';

  if (!contentType.includes('application/json')) {
    throw new Error('La API de artistas devolvió una respuesta inesperada');
  }

  const data = await response.json();
  if (!response.ok) {
    throw new Error(data?.error || `Error ${response.status} al cargar artistas`);
  }
  return Array.isArray(data) ? data : [];
};

/**
 * Fetch a single artist by slug or UUID from the existing API route.
 * The endpoint accepts either form.
 */
export const fetchArtist = async (identifier) => {
  if (!identifier) {
    throw new Error('Identificador de artista es requerido');
  }

  const response = await fetch(`/api/artist/${encodeURIComponent(identifier)}`);
  const contentType = response.headers.get('content-type') || '';

  if (!contentType.includes('application/json')) {
    throw new Error('La API del artista devolvió una respuesta inesperada');
  }

  if (response.status === 404) {
    return null;
  }

  const data = await response.json();

  if (!response.ok) {
    throw new Error(data?.error || `Error ${response.status} al cargar el artista`);
  }

  return data;
};

/**
 * Submit a follow signup for an artist (by UUID or slug). The endpoint is
 * idempotent — signing up the same email twice returns alreadyFollowing.
 */
export const contactArtist = async (identifier, { name, contactMethod, contactValue, message }) => {
  if (!identifier) throw new Error('Identificador de artista es requerido');
  if (!name) throw new Error('El nombre es requerido');
  if (!contactValue) throw new Error('El dato de contacto es requerido');

  const response = await fetch(`/api/artist/${encodeURIComponent(identifier)}/contact`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      name: name.trim(),
      contact_method: contactMethod,
      contact_value: contactValue.trim(),
      message: (message || '').trim(),
    }),
  });

  const contentType = response.headers.get('content-type') || '';
  if (!contentType.includes('application/json')) {
    throw new Error('La API devolvió una respuesta inesperada');
  }

  const data = await response.json();
  if (!response.ok || data?.success === false) {
    throw new Error(data?.error || `Error ${response.status} al enviar el mensaje`);
  }
  return data;
};

export const followArtist = async (identifier, email) => {
  if (!identifier) throw new Error('Identificador de artista es requerido');
  if (!email) throw new Error('Correo electrónico es requerido');

  const response = await fetch(`/api/artist/${encodeURIComponent(identifier)}/follow`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email: email.trim().toLowerCase() }),
  });

  const contentType = response.headers.get('content-type') || '';
  if (!contentType.includes('application/json')) {
    throw new Error('La API de seguimiento devolvió una respuesta inesperada');
  }

  const data = await response.json();
  if (!response.ok || data?.success === false) {
    throw new Error(data?.error || `Error ${response.status} al registrar el seguimiento`);
  }
  return data;
};

/**
 * Fetch all products that belong to a given artist. Matches by artist_id when
 * available and falls back to artist_name so legacy products (no artist_id)
 * still surface in the gallery.
 */
export const fetchProductsByArtist = async (artist) => {
  if (!artist) return [];

  const all = await fetchProducts();
  const artistId = artist.id || artist.artist_id;
  const artistName = (artist.artist_name || artist.name || '').trim().toLowerCase();

  return all.filter((product) => {
    if (artistId && product.artist_id && product.artist_id === artistId) {
      return true;
    }
    if (artistName) {
      const candidate = (product.artist_name || product.artist || '').trim().toLowerCase();
      return candidate && candidate === artistName;
    }
    return false;
  });
};
